import { useNavigate } from 'react-router-dom';
import { Store, Clock, Plus, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useShift } from '../contexts/ShiftContext';

export function TurnoBar() {
  const { user } = useAuth();
  const { asistencia, isOpen, isClosed, puntoNombre, loading } = useShift();
  const navigate = useNavigate();

  if (!user || user === false) return null;

  const isSupervisor = user.role === 'admin' || user.role === 'propietario';
  const label = user.role === 'propietario' ? 'PROPIETARIO' : user.role === 'admin' ? 'ADMINISTRADOR' : '';

  // Supervisor sin sucursal ligada a su cuenta: no hay turno que mostrar, solo fecha + rol.
  if (isSupervisor && !user.punto_venta_id) {
    const hoy = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
    return (
      <div className="sticky top-0 z-50 bg-zinc-900 text-white px-4 py-1.5 flex items-center justify-between" data-testid="turno-bar-admin">
        <span className="text-xs font-medium text-zinc-400 capitalize">{hoy}</span>
        <span className="text-xs font-bold text-zinc-300 tracking-wide">{label}</span>
      </div>
    );
  }

  if (loading) return <div className="sticky top-0 z-50 h-8 bg-zinc-900" />;

  if (!asistencia) {
    return (
      <div className="sticky top-0 z-50 bg-zinc-800 text-white px-4 py-1.5 flex items-center justify-between" data-testid="turno-bar-empty">
        <span className="text-xs text-zinc-400">Sin turno activo</span>
        {isSupervisor ? (
          <span className="text-xs font-bold text-zinc-300 tracking-wide">{label}</span>
        ) : (
          <button
            onClick={() => navigate('/asistencia')}
            className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white px-2.5 py-0.5 rounded text-xs font-medium"
            data-testid="btn-iniciar-turno"
          >
            <Plus className="w-3 h-3" /> Iniciar turno
          </button>
        )}
      </div>
    );
  }

  const formatDate = (fecha) => {
    if (!fecha) return '';
    const [y, m, d] = fecha.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    return new Date(isoStr).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      className={`sticky top-0 z-50 px-4 py-1.5 flex items-center justify-between gap-2 ${isClosed ? 'bg-zinc-700' : 'bg-zinc-900'}`}
      data-testid="turno-bar"
    >
      {/* Left: POS info */}
      <div className="flex items-center gap-2 text-white min-w-0 overflow-hidden">
        <Store className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="text-xs font-semibold truncate">{puntoNombre}</span>
        {asistencia.folio != null && (
          <>
            <span className="text-zinc-500">·</span>
            <span className="text-xs text-zinc-300 whitespace-nowrap" data-testid="turno-folio">Turno #{asistencia.folio}</span>
          </>
        )}
        <span className="text-zinc-500">·</span>
        <span className="text-xs text-zinc-300 whitespace-nowrap">{formatDate(asistencia.fecha)}</span>
        <span className="text-zinc-500">·</span>
        <Clock className="w-3 h-3 text-zinc-400 shrink-0" />
        <span className="text-xs text-zinc-300 whitespace-nowrap">desde {formatTime(asistencia.hora_entrada)}</span>
      </div>

      {/* Right: status + action */}
      <div className="flex items-center gap-2 shrink-0">
        {isOpen && (
          <span className="flex items-center gap-1 text-xs font-medium text-green-400" data-testid="turno-abierto">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            Abierto
          </span>
        )}
        {isClosed && (
          <>
            <span className="flex items-center gap-1 text-xs text-zinc-400" data-testid="turno-cerrado">
              <Lock className="w-3 h-3" />
              Cerrado
            </span>
            {isSupervisor ? (
              <span className="text-xs font-bold text-zinc-300 tracking-wide">{label}</span>
            ) : (
              <button
                onClick={() => navigate('/asistencia')}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-0.5 rounded text-xs font-medium"
                data-testid="btn-nuevo-turno"
              >
                <Plus className="w-3 h-3" /> Nuevo turno
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
